import React, { useState, useEffect, useContext, useCallback, useMemo, useRef } from 'react';
import AppContext from 'AppContext';
import GrpSpinner from 'Components/GrpSpinner/GrpSpinner';
import GrpButton from 'Library/GrpButton/GrpButton';
import GrpModal from 'Components/GrpModal/GrpModal';
import {
  getFundsList,
  getFundGroups,
  getFundsOverview,
  getFundClassesOverview,
  generateReportsDurable,
  downloadArtifacts,
  approveReports,
  approveOrReopenReportsDurable,
  getBaseLanguageNarratives,
  updateBaseNarrativeStatus,
  reopenReport,
  getTranslationNarratives,
  updateNarrativeTranslationStatusBulk,
  calculateRiskAndPerf,
  runBusinessRules,
  extractResults,
} from 'Services/overviewApi';
import { getDurableFunctionStatus } from 'Services/backofficeApi';
import classes from './OverviewV2.module.scss';

const DEFAULT_FUND = '0 All Funds';
const DEFAULT_FUND_GROUP = '0 All Fund Groups';
const ORCHESTRATION_TERMINAL_STATUS = new Set(['COMPLETED', 'FAILED', 'TERMINATED', 'CANCELED', 'CANCELLED']);

const STATUS_MAP = {
  // Keyed by IndicatorStatus (what the Blazor component receives as Status param)
  unknown: { cls: 'statusNA', icon: '/' },
  approved: { cls: 'statusApproved', icon: '✓✓' },
  generated: { cls: 'statusGenerated', icon: '✓' },
  error: { cls: 'statusError', icon: '✗' },
  warning: { cls: 'statusWarning', icon: '!' },
  'info-warning': { cls: 'statusInfoWarning', icon: '!' },
  'info-error': { cls: 'statusInfoError', icon: '✗' },
  notgenerated: { cls: 'statusNotGen', icon: '/' },
};

// Maps the IndicatorMessage (display text from the enum) to the IndicatorStatus
const STATUS_MESSAGE_TO_INDICATOR = {
  'N/A': 'unknown',
  'Not Generated': 'unknown',
  'Being Generated': 'unknown',
  Approved: 'approved',
  Generated: 'generated',
  'Generated - Errors': 'error',
  'Generated - Warnings': 'warning',
  // Alternate formats that the API may return
  'Generated with errors': 'error',
  'Generated with warnings': 'warning',
  NA: 'unknown',
};

const RULE_VALIDATION_TYPES = [
  { key: 0, label: 'Data Analysis' },
  { key: 1, label: 'Risk & Performance' },
  { key: 2, label: 'Transaction Cost' },
];

const SEVERITY_MAP = { Error: 1, Warning: 0 };

// Derives the indicator status for fund-level report type data from count fields
const deriveHeaderStatus = (rts) => {
  if (rts.ApprovedCount > 0) return 'approved';
  if (rts.GeneratedWithErrorsCount > 0) return 'error';
  if (rts.GeneratedWithWarningsCount > 0) return 'warning';
  if (rts.GeneratedCount > 0) return 'generated';
  if (rts.NotGeneratedCount > 0) return 'notgenerated';
  return 'unknown';
};

// Derives the indicator status + message for class-level detail (mirrors ReportTypeStatusType enum)
const deriveDetailStatus = (rts, ruleValidationItems) => {
  if (!rts.IsReportSetupForClass) return { status: 'unknown', message: 'N/A' };
  if (rts.IsReportApproved) return { status: 'approved', message: 'Approved' };

  const docStatusId = rts.DocumentStatusId || 0;
  
  // Being generated (in-progress states)
  if (docStatusId > 10 && docStatusId < 90) return { status: 'unknown', message: 'Being Generated' };

  // Generated (final states)
  if (docStatusId >= 90) {
    if (ruleValidationItems && ruleValidationItems.length > 0) {
      const hasErrors = ruleValidationItems.some((i) => i.RuleValidationSeverity === 1 && i.RuleItemCount > 0);
      if (hasErrors) return { status: 'error', message: 'Generated - Errors' };
      const hasWarnings = ruleValidationItems.some((i) => i.RuleValidationSeverity === 0 && i.RuleItemCount > 0);
      if (hasWarnings) return { status: 'warning', message: 'Generated - Warnings' };
    }
    return { status: 'generated', message: 'Generated' };
  }

  return { status: 'unknown', message: 'Not Generated' };
};

const parseErrorWarningJson = (jsonString) => {
  if (!jsonString) return [];
  try {
    const items = JSON.parse(jsonString);
    return items;
  } catch {
    return [];
  }
};

// Deduplicates report type statuses by ReportTypeId, keeping the latest (highest AutoID)
const dedupeByReportType = (items) => {
  const map = new Map();
  items.forEach((item) => {
    const existing = map.get(item.ReportTypeId);
    if (!existing || (item.AutoID || 0) > (existing.AutoID || 0)) {
      map.set(item.ReportTypeId, item);
    }
  });
  return Array.from(map.values());
};

const getValidationCount = (ruleValidationItems, type, severity) => {
  if (!ruleValidationItems || !Array.isArray(ruleValidationItems)) return 0;
  const severityNum = SEVERITY_MAP[severity] ?? severity;
  const item = ruleValidationItems.find(
    (i) => i.RuleValidationType === type && i.RuleValidationSeverity === severityNum
  );
  return item?.RuleItemCount || 0;
};

const getDurableStatusQueryUri = (payload) => {
  if (!payload) return null;

  const direct = payload.statusQueryGetUri || payload.StatusQueryGetUri;
  if (direct) return direct;

  const content = payload.Content || payload.content;
  if (content) {
    const contentUri = content.statusQueryGetUri || content.StatusQueryGetUri;
    if (contentUri) return contentUri;
  }

  const value = payload.value;
  if (Array.isArray(value) && value.length > 0) {
    return value[0]?.statusQueryGetUri || value[0]?.StatusQueryGetUri || null;
  }

  return null;
};

const getDurableRuntimeStatus = (payload) => {
  if (!payload) return null;

  const direct = payload.runtimeStatus || payload.RuntimeStatus;
  if (direct) return direct;

  const content = payload.Content || payload.content;
  if (content) {
    const contentStatus = content.runtimeStatus || content.RuntimeStatus;
    if (contentStatus) return contentStatus;
  }

  const value = payload.value;
  if (Array.isArray(value) && value.length > 0) {
    const first = value[0];
    return first?.runtimeStatus || first?.RuntimeStatus || null;
  }

  return null;
};

const waitForDurableCompletion = async (statusQueryGetUri, maxAttempts = 90, intervalMs = 10000) => {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const statusPayload = await getDurableFunctionStatus(statusQueryGetUri);
    const runtimeStatus = getDurableRuntimeStatus(statusPayload);

    if (runtimeStatus === 'Completed') return;
    if (runtimeStatus === 'Failed' || runtimeStatus === 'Terminated' || runtimeStatus === 'Canceled' || runtimeStatus === 'Cancelled') {
      throw new Error(`Durable flow ended with status: ${runtimeStatus}`);
    }

    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }

  throw new Error('Durable flow did not complete in time');
};

const OverviewV2 = () => {
  const context = useContext(AppContext);

  const [loading, setLoading] = useState(true);
  const [searchValue, setSearchValue] = useState('');
  const [selectedFund, setSelectedFund] = useState(DEFAULT_FUND);
  const [selectedFundGroup, setSelectedFundGroup] = useState(DEFAULT_FUND_GROUP);
  const [fundOptions, setFundOptions] = useState([]);
  const [fundGroupOptions, setFundGroupOptions] = useState([]);
  const [fundCards, setFundCards] = useState([]);
  const [selectedShareClasses, setSelectedShareClasses] = useState(new Set());
  const [selectAllMode, setSelectAllMode] = useState(false);
  const [reportTypes, setReportTypes] = useState([]);
  const [contextMenu, setContextMenu] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null); // 'calc' | 'rules' | 'extract' | 'generate' | 'download' | 'approve' | null
  const [confirmModal, setConfirmModal] = useState(null);
  const [orchestrationStatus, setOrchestrationStatus] = useState(null);
  const generationTimerRef = useRef(null);
  const contextMenuRef = useRef(null);
  const dropdownRef = useRef(null);

  const reportingPeriod = context.state?.selectedReportingDate?.code;
  const regulation = context.state?.selectedRegulation;

  const formatRP = useCallback((rp) => {
    if (!rp) return '';
    if (typeof rp === 'string') return rp;
    return new Date(rp).toISOString().split('T')[0];
  }, []);

  // Close context menu on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(e.target)) {
        setContextMenu(null);
      }
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load initial data
  const loadData = useCallback(async () => {
    const rpFormatted = formatRP(reportingPeriod);
    if (!rpFormatted || !regulation) {
      setLoading(false);
      setFundCards([]);
      return;
    }

    try {
      setLoading(true);

      const [fundsRes, fundGroupsRes, overviewRes] = await Promise.all([
        getFundsList(rpFormatted, regulation.Name, '', ''),
        getFundGroups(),
        getFundsOverview(rpFormatted, regulation.Name, '', '', ''),
      ]);

      // Fund options
      const fundsData = fundsRes.value || fundsRes.Content || [];
      const fOpts = fundsData
        .map((f) => {
          const code = f.fundcode || f.FundCode || f.Fund_Code;
          const name = f.fundname || f.FundName || f.Fund_Name;
          if (!code) return null;
          return { label: name ? `${name} (${code})` : code, value: code };
        })
        .filter(Boolean);
      fOpts.push({ label: 'All Funds', value: DEFAULT_FUND });
      setFundOptions(fOpts);

      // Fund group options
      const fgData = fundGroupsRes.value || fundGroupsRes.Content || [];
      const fgOpts = fgData
        .filter((fg) => {
          const refDate = fg.Reference_Date || fg.reference_date || '';
          return !refDate || refDate.substring(0, 10) === rpFormatted;
        })
        .map((fg) => {
          const code = fg.Fund_Group_Code || fg.fund_group_code || fg.FundGroupCode;
          const name = fg.FundGroupName || fg.Portfolio_Manufacturer_Group_Name || fg.Fund_Group_Name;
          if (!code) return null;
          return { label: name ? `${name} (${code})` : code, value: code };
        })
        .filter(Boolean);
      fgOpts.push({ label: 'All Fund Groups', value: DEFAULT_FUND_GROUP });
      setFundGroupOptions(fgOpts);

      // Report types — use regulation's embedded list (mirrors Blazor's RegulationReportTypesList)
      setReportTypes(regulation?.ReportTypes || []);

      // Overview data (fund cards)
      const overviewData = overviewRes.value || overviewRes.Content || [];
      const cards = overviewData.map((fund) => ({
        fundCode: fund.fundcode,
        fundName: fund.fundname,
        fundGroupCode: fund.fundgroupcode,
        classCount: fund.classescount || 0,
        shownClassCount: fund.classescount || 0,
        reportTypeStatuses: parseErrorWarningJson(fund.reporttypesjsondata),
        ruleValidationItems: parseErrorWarningJson(fund.errorwarningjsondata),
        expanded: false,
        loadingClasses: false,
        gridData: [],
        isSelected: false,
      }));
      setFundCards(cards);
    } catch (err) {
      context.handlers.setToast({ type: 'error', body: <>{err.human || 'Failed to load overview data'}</> });
    } finally {
      setLoading(false);
    }
  }, [reportingPeriod, regulation, formatRP, context.handlers]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filter fund cards
  const filteredFundCards = useMemo(() => {
    let cards = fundCards;

    // Filter by fund group
    if (selectedFundGroup && selectedFundGroup !== DEFAULT_FUND_GROUP) {
      cards = cards.filter((c) => c.fundGroupCode === selectedFundGroup);
    }

    // Filter by fund
    if (selectedFund && selectedFund !== DEFAULT_FUND) {
      cards = cards.filter((c) => c.fundCode === selectedFund);
    }

    return cards;
  }, [fundCards, selectedFund, selectedFundGroup]);

  useEffect(() => {
    if (fundCards.length === 0) {
      setSelectAllMode(false);
      return;
    }

    const allSelected = fundCards.every((card) => {
      if (card.isSelected) return true;
      if (!card.gridData || card.gridData.length === 0) return false;
      return card.gridData.every((cl) => cl.isSelected);
    });

    setSelectAllMode((prev) => (prev === allSelected ? prev : allSelected));
  }, [fundCards]);

  // Refresh funds list based on filters
  const refreshData = async (fundGroup, fund, search) => {
    const rpFormatted = formatRP(reportingPeriod);
    if (!rpFormatted || !regulation) return;

    try {
      setLoading(true);
      const fg = (!fundGroup || fundGroup === DEFAULT_FUND_GROUP) ? '' : fundGroup;
      const fc = (!fund || fund === DEFAULT_FUND) ? '' : fund;

      const overviewRes = await getFundsOverview(rpFormatted, regulation.Name, fc, search, fg);
      const overviewData = overviewRes.value || overviewRes.Content || [];

      const cards = overviewData.map((f) => ({
        fundCode: f.fundcode,
        fundName: f.fundname,
        fundGroupCode: f.fundgroupcode,
        classCount: f.classescount || 0,
        shownClassCount: f.classescount || 0,
        reportTypeStatuses: parseErrorWarningJson(f.reporttypesjsondata),
        ruleValidationItems: parseErrorWarningJson(f.errorwarningjsondata),
        expanded: false,
        loadingClasses: false,
        gridData: [],
        isSelected: false,
      }));
      setFundCards(cards);
      setSelectedShareClasses(new Set());
      setSelectAllMode(false);
    } catch (err) {
      context.handlers.setToast({ type: 'error', body: <>{err.human || 'Failed to refresh data'}</> });
    } finally {
      setLoading(false);
    }
  };

  const handleFundGroupChange = async (e) => {
    const fundGroup = e.target.value;
    setSelectedFundGroup(fundGroup);
    setSelectedFund(DEFAULT_FUND);
    setSearchValue('');

    // Refresh fund options based on fund group
    const rpFormatted = formatRP(reportingPeriod);
    const fundsRes = await getFundsList(rpFormatted, regulation.Name, '', '');
    const fundsData = fundsRes.value || fundsRes.Content || [];
    const fg = (!fundGroup || fundGroup === DEFAULT_FUND_GROUP) ? '' : fundGroup;
    const filtered = fg ? fundsData.filter((f) => f.FundGroupCode === fg) : fundsData;
    const fOpts = filtered.map((f) => ({
      label: `${f.FundName} (${f.FundCode})`,
      value: f.FundCode,
    }));
    fOpts.push({ label: 'All Funds', value: DEFAULT_FUND });
    setFundOptions(fOpts);

    await refreshData(fundGroup, DEFAULT_FUND, '');
  };

  const handleFundChange = async (e) => {
    const fund = e.target.value;
    setSelectedFund(fund);
    setSearchValue('');
    await refreshData(selectedFundGroup, fund, '');
  };

  const handleSearch = async () => {
    await refreshData(selectedFundGroup, selectedFund, searchValue);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  const toggleCardExpand = async (fundCode) => {
    const cardIndex = fundCards.findIndex((c) => c.fundCode === fundCode);
    if (cardIndex === -1) return;

    const card = fundCards[cardIndex];
    const newExpanded = !card.expanded;

    if (newExpanded && card.gridData.length === 0) {
      // Load classes for this fund
      const updatedCards = [...fundCards];
      updatedCards[cardIndex] = { ...card, expanded: true, loadingClasses: true };
      setFundCards(updatedCards);

      try {
        const rpFormatted = formatRP(reportingPeriod);
        const classesRes = await getFundClassesOverview(rpFormatted, regulation.Name, fundCode, searchValue);
        const classesData = classesRes.value || classesRes.Content || [];

        const gridData = classesData.map((cl) => ({
          classCode: cl.classcode,
          className: cl.classname,
          fundCode: cl.fundcode,
          recordid: cl.recordid,
          reportTypeStatuses: dedupeByReportType(parseErrorWarningJson(cl.reporttypesjsondata)),
          ruleValidationItems: parseErrorWarningJson(cl.errorwarningjsondata),
          isSelected: selectedShareClasses.has(cl.classcode) || false,
        }));

        updatedCards[cardIndex] = {
          ...card,
          expanded: true,
          loadingClasses: false,
          gridData,
          shownClassCount: gridData.length,
        };
        setFundCards([...updatedCards]);
      } catch (err) {
        // Log error and show empty grid
        console.error('Failed to load fund classes:', err);
        updatedCards[cardIndex] = { ...card, expanded: true, loadingClasses: false, gridData: [] };
        setFundCards([...updatedCards]);
      }
    } else {
      const updatedCards = [...fundCards];
      updatedCards[cardIndex] = { ...card, expanded: newExpanded };
      setFundCards(updatedCards);
    }
  };

  const toggleFundSelection = (fundCode) => {
    const cardIndex = fundCards.findIndex((c) => c.fundCode === fundCode);
    if (cardIndex === -1) return;

    const card = fundCards[cardIndex];
    const newSelected = !card.isSelected;

    const updatedCards = [...fundCards];
    updatedCards[cardIndex] = {
      ...card,
      isSelected: newSelected,
      gridData: card.gridData.map((cl) => ({ ...cl, isSelected: newSelected })),
    };
    setFundCards(updatedCards);

    // Update selected share classes set
    if (newSelected) {
      const newSet = new Set(selectedShareClasses);
      card.gridData.forEach((cl) => newSet.add(cl.classCode));
      setSelectedShareClasses(newSet);
    } else {
      const newSet = new Set(selectedShareClasses);
      card.gridData.forEach((cl) => newSet.delete(cl.classCode));
      setSelectedShareClasses(newSet);
    }
  };

  const toggleClassSelection = (fundCode, classCode) => {
    const cardIndex = fundCards.findIndex((c) => c.fundCode === fundCode);
    if (cardIndex === -1) return;

    const card = fundCards[cardIndex];
    const updatedGridData = card.gridData.map((cl) =>
      cl.classCode === classCode ? { ...cl, isSelected: !cl.isSelected } : cl
    );

    const allClassesSelected = updatedGridData.length > 0 && updatedGridData.every((cl) => cl.isSelected);

    const updatedCards = [...fundCards];
    updatedCards[cardIndex] = {
      ...card,
      isSelected: allClassesSelected,
      gridData: updatedGridData,
    };
    setFundCards(updatedCards);

    // Update selected share classes set
    const newSet = new Set(selectedShareClasses);
    if (newSet.has(classCode)) {
      newSet.delete(classCode);
    } else {
      newSet.add(classCode);
    }
    setSelectedShareClasses(newSet);
  };

  const handleToggleSelectAll = () => {
    const newSelectAll = !selectAllMode;

    if (!newSelectAll) {
      setSelectedShareClasses(new Set());
      setSelectAllMode(false);
      setFundCards((prev) => prev.map((card) => ({
        ...card,
        isSelected: false,
        gridData: card.gridData.map((cl) => ({ ...cl, isSelected: false })),
      })));
      return;
    }

    const newSelected = new Set();
    fundCards.forEach((card) => {
      (card.gridData || []).forEach((cl) => newSelected.add(cl.classCode));
    });

    setSelectedShareClasses(newSelected);
    setSelectAllMode(true);
    setFundCards((prev) => prev.map((card) => ({
      ...card,
      isSelected: true,
      gridData: card.gridData.map((cl) => ({ ...cl, isSelected: true })),
    })));
  };

  const getStatusBadge = (status, count, total, message) => {
    // Resolve status to indicator status (like Blazor's ReportTypeStatusType enum)
    const indicatorStatus = STATUS_MESSAGE_TO_INDICATOR[status] || status || 'unknown';
    const mapped = STATUS_MAP[indicatorStatus] || STATUS_MAP.unknown;

    let messageText = '';
    let partialBackground = '';

    if (total != null && total >= 0) {
      if (count != null && count >= 0) {
        messageText = `${count}/${total}`;
        if (indicatorStatus === 'approved' && total > 0) {
          const pct = (count / total) * 100;
          partialBackground = `linear-gradient(to right, rgba(46,196,182,0.2) ${pct}%, white ${pct}%)`;
        }
        if (message) {
          messageText += ` ${message}`;
        }
      } else {
        messageText = `${total}`;
        if (message) {
          messageText += ` ${message}`;
        }
      }
    } else {
      messageText = message || '';
    }

    const noTextClass = !messageText ? classes.statusNoText : '';
    const style = partialBackground ? { background: partialBackground } : undefined;

    return (
      <span className={`${classes.statusIndicator} ${classes[mapped.cls]} ${noTextClass}`} style={style}>
        <span className={classes.statusIconContainer}>{mapped.icon}</span>
        {messageText && <span className={classes.statusMessage}>{messageText}</span>}
      </span>
    );
  };

  // ─── Helper: Build fund grouping from selected share classes ──────────
  const getGenerateFundGrouping = useCallback(() => {
    const selectedList = [];
    fundCards.forEach((card) => {
      if (card.isSelected && card.gridData.length === 0) {
        // Entire fund selected but classes not loaded yet
        selectedList.push({ fundCode: card.fundCode, classCode: '' });
      } else {
        card.gridData.forEach((cl) => {
          if (cl.isSelected) {
            selectedList.push({ fundCode: card.fundCode, classCode: cl.classCode });
          }
        });
      }
    });

    if (selectedList.length === 0) return [];

    const grouped = selectedList.reduce((acc, item) => {
      if (!acc[item.fundCode]) acc[item.fundCode] = [];
      acc[item.fundCode].push(item.classCode);
      return acc;
    }, {});

    return Object.entries(grouped).map(([fundCode, classCodes]) => ({
      FundCode: fundCode,
      ClassCodes: classCodes,
    }));
  }, [fundCards]);

  const getAvailableClassesForReport = useCallback((reportTypeId) => {
    let count = 0;
    const hasAnyFundSelected = fundCards.some((c) => c.isSelected);

    if (hasAnyFundSelected) {
      fundCards.forEach((fund) => {
        if (fund.isSelected && fund.gridData.length === 0) {
          const rts = fund.reportTypeStatuses.find((r) => r.ReportTypeId === reportTypeId);
          count += rts?.CountOfEnabledClassesForThisReport || rts?.classescount || fund.classCount || 0;
        } else {
          fund.gridData.forEach((cl) => {
            if (cl.isSelected) {
              const isSetup = cl.reportTypeStatuses?.find((r) => r.ReportTypeId === reportTypeId)?.IsReportSetupForClass;
              if (isSetup !== false) count++;
            }
          });
        }
      });
    } else {
      fundCards.forEach((fund) => {
        fund.gridData.forEach((cl) => {
          if (cl.isSelected) {
            const isSetup = cl.reportTypeStatuses?.find((r) => r.ReportTypeId === reportTypeId)?.IsReportSetupForClass;
            if (isSetup !== false) count++;
          }
        });
      });
    }
    return count;
  }, [fundCards]);

  const getClassCountForReport = useCallback((reportTypeId) => {
    return fundCards.reduce((sum, card) => {
      const rts = card.reportTypeStatuses.find((r) => r.ReportTypeId === reportTypeId);
      return sum + (rts?.CountOfEnabledClassesForThisReport || card.classCount || 0);
    }, 0);
  }, [fundCards]);

  // ─── Generation Status Polling ──────────────────────────────────────
  const stopGenerationPolling = useCallback(() => {
    if (generationTimerRef.current) {
      clearInterval(generationTimerRef.current);
      generationTimerRef.current = null;
    }
  }, []);

  const startDurableGenerationPolling = useCallback((statusQueryGetUri, label) => {
    stopGenerationPolling();

    let attempts = 0;
    let consecutiveNetworkFailures = 0;
    const maxAttempts = 180; // 15 minutes at 5s interval

    generationTimerRef.current = setInterval(async () => {
      attempts += 1;
      try {
        const statusPayload = await getDurableFunctionStatus(statusQueryGetUri);
        const runtimeStatusRaw = getDurableRuntimeStatus(statusPayload) || 'Pending';
        const runtimeStatus = runtimeStatusRaw.toUpperCase();
        consecutiveNetworkFailures = 0;

        setOrchestrationStatus({
          label,
          runtimeStatus,
          message: `${label} generation status: ${runtimeStatusRaw}`,
          isFinal: ORCHESTRATION_TERMINAL_STATUS.has(runtimeStatus),
        });

        if (runtimeStatus === 'COMPLETED') {
          stopGenerationPolling();
          context.handlers.setToast({ type: 'success', body: <>{label} generation completed.</> });
          await refreshData(selectedFundGroup, selectedFund, searchValue);
          return;
        }

        if (runtimeStatus === 'FAILED' || runtimeStatus === 'TERMINATED' || runtimeStatus === 'CANCELED' || runtimeStatus === 'CANCELLED') {
          stopGenerationPolling();
          context.handlers.setToast({ type: 'error', body: <>Error generating {label}: orchestration ended with status {runtimeStatusRaw}.</> });
          return;
        }

        if (attempts >= maxAttempts) {
          stopGenerationPolling();
          setOrchestrationStatus({
            label,
            runtimeStatus: 'TERMINATED',
            message: `${label} generation monitor timed out. Please refresh to verify final status.`,
            isFinal: true,
          });
          context.handlers.setToast({ type: 'error', body: <>Generation is taking too long. Please refresh and check the latest status.</> });
        }
      } catch {
        consecutiveNetworkFailures += 1;
        setOrchestrationStatus({
          label,
          runtimeStatus: 'RUNNING',
          message: `${label} generation is running, but status checks are temporarily failing (network issue).`,
          isFinal: false,
        });

        if (consecutiveNetworkFailures >= 3 || attempts >= maxAttempts) {
          stopGenerationPolling();
          setOrchestrationStatus({
            label,
            runtimeStatus: 'TERMINATED',
            message: `Unable to monitor ${label} generation due to repeated network failures.`,
            isFinal: true,
          });
          context.handlers.setToast({ type: 'error', body: <>Unable to monitor generation status because of network failures. Please refresh and verify if processing completed.</> });
        }
      }
    }, 5000);
  }, [stopGenerationPolling, context.handlers, refreshData, selectedFundGroup, selectedFund, searchValue]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => stopGenerationPolling();
  }, [stopGenerationPolling]);

  // ─── Confirm Modal helper ─────────────────────────────────────────
  const showConfirm = useCallback((title, message) => new Promise((resolve) => {
    setConfirmModal({ title, message, onConfirm: resolve });
  }), []);

  // ─── Run Calculations ────────────────────────────────────────────────
  const handleCalculate = async (all) => {
    const rpFormatted = formatRP(reportingPeriod);
    const clientCode = context.state?.selectedClient?.code;
    const userAzureId = context.state?.userAzureID;
    const userEmail = context.state?.userEmail || 'Unknown';
    const regulationName = regulation?.Name;
    if (!rpFormatted || !clientCode) return;

    const totalCount = fundCards.reduce((sum, card) => sum + (card.classCount || 0), 0);
    const selectedCount = fundCards.reduce((sum, card) => {
      const gridData = card.gridData || [];
      if (card.isSelected && gridData.length === 0) {
        return sum + (card.classCount || 0);
      }
      return sum + gridData.filter((cl) => cl.isSelected).length;
    }, 0);
    const documentCount = all ? totalCount : selectedCount;

    if (documentCount < 1) {
      await showConfirm('No documents to calculate', 'There are no documents to calculate.');
      return;
    }

    const confirmed = await showConfirm(
      'Run Calculations',
      `You are about to Run Calculations for ${documentCount} Share Class${documentCount === 1 ? '' : 'es'}.`,
    );
    if (!confirmed) return;

    try {
      context.handlers.setToast({ type: 'info', body: <>R&P Calculations Running...</> });
      const response = await calculateRiskAndPerf({
        reportingPeriod: rpFormatted,
        regulation: regulationName,
        userAzureId,
        userEmail,
        calculateForAllShareClasses: all,
        fundCards,
      });

      const statusQueryGetUri = getDurableStatusQueryUri(response);
      if (statusQueryGetUri) {
        await waitForDurableCompletion(statusQueryGetUri);
        await refreshData(selectedFundGroup, selectedFund, searchValue);
      }

      context.handlers.setToast({ type: 'success', body: <>R&P Calculations completed successfully.</> });
    } catch (err) {
      context.handlers.setToast({ type: 'error', body: <>Error running R&P Calculations: {err.human || err.message || 'Unknown error'}</> });
    }
  };

  // ─── Run Business Rules ─────────────────────────────────────────────
  const handleRunBusinessRules = async () => {
    const rpFormatted = formatRP(reportingPeriod);
    const clientCode = context.state?.selectedClient?.code;
    const userAzureId = context.state?.userAzureID;
    const userEmail = context.state?.userEmail || 'Unknown';
    const regulationName = regulation?.Name;
    if (!rpFormatted || !clientCode) return;

    const confirmed = await showConfirm(
      'Run Business Rules',
      'You are about to Run Business Rules for All Share Classes.',
    );
    if (!confirmed) return;

    try {
      context.handlers.setToast({ type: 'info', body: <>Rule Regulation Population is Running...</> });
      const responses = await runBusinessRules({
        reportingPeriod: rpFormatted,
        regulation: regulationName,
        userAzureId,
        userEmail,
      });

      // runBusinessRules returns an array of responses; wait for all to complete
      if (Array.isArray(responses) && responses.length > 0) {
        const statusUris = responses
          .map((resp) => getDurableStatusQueryUri(resp))
          .filter(Boolean);
        
        if (statusUris.length > 0) {
          await Promise.all(statusUris.map((uri) => waitForDurableCompletion(uri)));
          await refreshData(selectedFundGroup, selectedFund, searchValue);
        }
      }

      context.handlers.setToast({ type: 'success', body: <>Business Rules completed successfully.</> });
    } catch (err) {
      context.handlers.setToast({ type: 'error', body: <>Error running Business Rules: {err.human || err.message || 'Unknown error'}</> });
    }
  };

  // ─── Extract Data ──────────────────────────────────────────────────────
  const handleExtractResults = async (actionLabel, extractAll) => {
    const rpFormatted = formatRP(reportingPeriod);
    const clientCode = context.state?.selectedClient?.code;
    const userAzureId = context.state?.userAzureID;
    const userEmail = context.state?.userEmail || 'Unknown';
    const regulationName = regulation?.Name;
    if (!rpFormatted || !clientCode) return;

    const overviewData = [];
    fundCards.forEach((card) => {
      (card.gridData || []).forEach((cl) => {
        overviewData.push({
          FundCode: card.fundCode,
          ClassCode: cl.classCode,
          IsSelected: !!cl.isSelected || !!card.isSelected,
        });
      });
    });

    try {
      context.handlers.setToast({ type: 'info', body: <>Queuing {actionLabel} extract...</> });
      const response = await extractResults({
        reportingPeriod: rpFormatted,
        regulation: regulationName,
        userAzureId,
        userEmail,
        extractAll,
        actionLabel,
        overviewData,
        fundCards,
      });

      const statusQueryGetUri = getDurableStatusQueryUri(response);
      if (statusQueryGetUri) {
        await waitForDurableCompletion(statusQueryGetUri);
        await refreshData(selectedFundGroup, selectedFund, searchValue);
      }

      context.handlers.setToast({
        type: 'success',
        body: <>You have queued a request to extract {actionLabel}. It will appear on the Data Management {'>'} Exports page when the extract has completed.</>,
      });
      setSelectedShareClasses(new Set());
      setFundCards((prev) => prev.map((c) => ({
        ...c,
        isSelected: false,
        gridData: c.gridData.map((cl) => ({ ...cl, isSelected: false })),
      })));
    } catch {
      context.handlers.setToast({ type: 'error', body: <>Error occurred while running Extract R&amp;P Results</> });
    }
  };

  // ─── Generate ───────────────────────────────────────────────────────
  const handleGenerate = async (reportTypeId, all) => {
    const rpFormatted = formatRP(reportingPeriod);
    const clientCode = context.state?.selectedClient?.code;
    if (!rpFormatted || !clientCode) return;

    const countAll = getClassCountForReport(reportTypeId);
    const countSelected = getAvailableClassesForReport(reportTypeId);
    const documentCount = all ? countAll : countSelected;

    if (documentCount < 1) {
      await showConfirm('No documents to generate', 'There are no documents to generate.');
      return;
    }

    const reportType = reportTypes.find((rt) => rt.ReportTypeId === reportTypeId);
    const label = reportType?.Label || 'Report';

    const confirmed = await showConfirm(
      `Generate ${label}`,
      `You are about to generate a ${label} document for ${documentCount} Share Class${documentCount > 1 ? 'es' : ''}.`,
    );
    if (!confirmed) return;

    try {
      context.handlers.setToast({ type: 'info', body: <>Generating {label} for {documentCount} Share Class{documentCount > 1 ? 'es' : ''}...</> });

      const typeName = reportType?.TypeName || '';
      const regulationName = (regulation?.Name || '').toUpperCase();
      
      // When all=true, extract all class codes from loaded grids; otherwise use selected ones
      let fundGrouping;
      if (all) {
        fundGrouping = fundCards
          .filter((card) => card.gridData.length > 0)
          .map((card) => ({
            FundCode: card.fundCode,
            ClassCodes: card.gridData.map((cl) => cl.classCode),
          }));
      } else {
        fundGrouping = getGenerateFundGrouping();
      }

      const allClassCodes = [...new Set(
        fundGrouping.flatMap((f) => (f.ClassCodes || []).filter(Boolean)),
      )];
      const allFundCodes = [...new Set(fundGrouping.map((f) => f.FundCode).filter(Boolean))].filter((fundCode) => {
        // Exclude funds with partial class visibility
        const card = fundCards.find((c) => c.fundCode === fundCode);
        return card && (card.classCount || 0) <= (card.shownClassCount || 0);
      });

      const generateResponse = await generateReportsDurable({
        reportingPeriod: rpFormatted,
        regulation: regulationName,
        reportTypeName: typeName,
        classCodes: allClassCodes,
        fundCodes: allFundCodes,
        userEmail: context.state?.userEmail || 'Unknown',
      });

      const statusQueryGetUri = getDurableStatusQueryUri(generateResponse);
      if (!statusQueryGetUri) {
        throw new Error('Generation did not return statusQueryGetUri.');
      }

      setOrchestrationStatus({
        label,
        runtimeStatus: 'PENDING',
        message: `${label} generation is pending.`,
        isFinal: false,
      });
      context.handlers.setToast({ type: 'info', body: <>Generation is in progress. You can continue working while we monitor completion.</> });
      startDurableGenerationPolling(statusQueryGetUri, label);

      // Reset selection
      setSelectedShareClasses(new Set());
      setSelectAllMode(false);
      setFundCards((prev) => prev.map((c) => ({
        ...c,
        isSelected: false,
        gridData: c.gridData.map((cl) => ({ ...cl, isSelected: false })),
      })));
    } catch (err) {
      setOrchestrationStatus({
        label,
        runtimeStatus: 'FAILED',
        message: `Error generating ${label}: ${err.message || 'Unknown error'}`,
        isFinal: true,
      });
      context.handlers.setToast({ type: 'error', body: <>Error generating {label}: {err.message || 'Unknown error'}</> });
    }
  };

  // ─── Download ───────────────────────────────────────────────────────
  const handleDownload = async (reportTypeId, all, fileFormat = 'PDF', extension = 'pdf') => {
    const rpFormatted = formatRP(reportingPeriod);
    const clientCode = context.state?.selectedClient?.code;
    if (!rpFormatted || !clientCode) return;

    const countAll = getClassCountForReport(reportTypeId);
    const countSelected = getAvailableClassesForReport(reportTypeId);
    const documentCount = all ? countAll : countSelected;

    if (documentCount < 1) {
      await showConfirm('No documents to download', 'There are no documents to download.');
      return;
    }

    const reportType = reportTypes.find((rt) => rt.ReportTypeId === reportTypeId);
    const label = reportType?.Label || 'Report';

    const confirmed = await showConfirm(
      `Download ${label}`,
      `You are about to download a ${label} document for ${documentCount} Share Class${documentCount > 1 ? 'es' : ''}.`,
    );
    if (!confirmed) return;

    try {
      context.handlers.setToast({ type: 'info', body: <>Downloading {label} for {documentCount} Share Class{documentCount > 1 ? 'es' : ''}...</> });

      const fundGrouping = all
        ? fundCards.map((f) => ({ FundCode: f.fundCode, ClassCodes: [''] }))
        : getGenerateFundGrouping();

      const downloadAllAvailable = all
        && !searchValue
        && (!selectedFundGroup || selectedFundGroup === DEFAULT_FUND_GROUP)
        && (!selectedFund || selectedFund === DEFAULT_FUND);

      const contract = {
        ReportTypeId: reportTypeId,
        ReferenceDate: rpFormatted,
        ClientCode: clientCode,
        FundCodes: fundGrouping,
        FileFormat: fileFormat,
        FileExtension: extension,
        isDownloadAllReports: downloadAllAvailable,
      };

      const fileContent = await downloadArtifacts(contract);

      if (!fileContent || fileContent.byteLength === 0) {
        context.handlers.setToast({ type: 'error', body: <>Returned content for {label} was empty.</> });
        return;
      }

      // Trigger browser file download
      const blob = new Blob([fileContent], { type: 'application/octet-stream' });
      const url = globalThis.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${clientCode}_${label}_${rpFormatted.replaceAll('-', '')}.${extension}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      globalThis.URL.revokeObjectURL(url);

      // Reset selection
      setSelectedShareClasses(new Set());
      setSelectAllMode(false);
      setFundCards((prev) => prev.map((c) => ({
        ...c,
        isSelected: false,
        gridData: c.gridData.map((cl) => ({ ...cl, isSelected: false })),
      })));

      context.handlers.setToast({ type: 'success', body: <>Downloaded {label} for {documentCount} Share Class{documentCount > 1 ? 'es' : ''}.</> });
    } catch (err) {
      context.handlers.setToast({ type: 'error', body: <>Error downloading {label}: {err.message || 'Unknown error'}</> });
    }
  };

  // ─── Approve ────────────────────────────────────────────────────────
  const handleApprove = async (reportTypeId, all) => {
    const rpFormatted = formatRP(reportingPeriod);
    const clientCode = context.state?.selectedClient?.code;
    const regulationName = regulation?.Name;
    if (!rpFormatted || !clientCode) return;

    const countAll = getClassCountForReport(reportTypeId);
    const countSelected = getAvailableClassesForReport(reportTypeId);
    const documentCount = all ? countAll : countSelected;

    if (documentCount < 1) {
      await showConfirm('No documents to approve', 'There are no documents to approve.');
      return;
    }

    const reportType = reportTypes.find((rt) => rt.ReportTypeId === reportTypeId);
    const label = reportType?.Label || 'Report';

    const confirmed = await showConfirm(
      `Approve ${label}`,
      `You are about to approve a ${label} document for ${documentCount} Share Class${documentCount > 1 ? 'es' : ''}.`,
    );
    if (!confirmed) return;

    try {
      context.handlers.setToast({ type: 'info', body: <>Approving {label} for {documentCount} Share Class{documentCount > 1 ? 'es' : ''}...</> });

      const isPriipsRegulation = regulationName === 'PRIIPS';
      const typeName = reportType?.TypeName || '';

      // Build reportsToProcess with ACTUAL class codes (the approval endpoint
      // requires real codes — unlike generate/download which accept '' as "all").
      const reportsToProcess = [];
      const allSelectedClassCodes = [];
      const allSelectedFundCodes = new Set();

      for (const card of fundCards) {
        let classCodesToApprove = [];

        if (all || card.isSelected) {
          if (card.gridData.length > 0) {
            // Fund is expanded — use loaded class codes
            if (all) {
              classCodesToApprove = card.gridData.map((cl) => cl.classCode);
            } else {
              const selected = card.gridData.filter((cl) => cl.isSelected);
              classCodesToApprove = selected.map((cl) => cl.classCode);
            }
          } else {
            // Fund is NOT expanded — load class data from server
            try {
              const classesRes = await getFundClassesOverview(rpFormatted, regulationName, card.fundCode, '');
              const classesData = classesRes.value || classesRes.Content || [];
              classCodesToApprove = classesData.map((cl) => cl.classcode);
            } catch {
              // If loading fails, skip this fund
            }
          }
        } else {
          // Fund not selected — check individual class selections
          const selected = card.gridData.filter((cl) => cl.isSelected);
          classCodesToApprove = selected.map((cl) => cl.classCode);
        }

        if (classCodesToApprove.length > 0) {
          reportsToProcess.push({
            FundCode: card.fundCode,
            ReportTypeId: reportTypeId,
            ClassCodes: classCodesToApprove,
            ActionType: 'A',
          });
          allSelectedClassCodes.push(...classCodesToApprove);
          allSelectedFundCodes.add(card.fundCode);
        }
      }

      if (reportsToProcess.length === 0) {
        context.handlers.setToast({ type: 'error', body: <>No Share Classes were resolved for approval.</> });
        return;
      }

      if (isPriipsRegulation) {
        await approveReports(reportsToProcess);
      } else {
        const approveResponse = await approveOrReopenReportsDurable({
          reportingPeriod: rpFormatted,
          regulation: regulationName,
          reportTypeName: typeName,
          action: 'A',
          classCodes: [...new Set(allSelectedClassCodes)],
          fundCodes: [...allSelectedFundCodes],
          userEmail: context.state?.userEmail || 'Unknown',
        });

        const statusQueryGetUri = getDurableStatusQueryUri(approveResponse);
        if (statusQueryGetUri) {
          context.handlers.setToast({ type: 'info', body: <>Approval is in progress. Waiting for completion...</> });
          await waitForDurableCompletion(statusQueryGetUri);
        }
      }

      // Update narrative statuses for PRIIPS regulation
      if (regulationName === 'PRIIPS') {
        try {
          const userEmail = context.state?.userEmail || 'Unknown';
          const selectedClassCodesSet = new Set(allSelectedClassCodes);

          // Get base language narratives and set status to 4 (Approved)
          const narrativesRes = await getBaseLanguageNarratives(rpFormatted, regulationName);
          const narratives = narrativesRes?.value || narrativesRes?.Content || [];
          const baseNarrativeIds = narratives
            .filter((n) => selectedClassCodesSet.has(n.ClassCode))
            .map((n) => n.NarrativeId);

          if (baseNarrativeIds.length > 0) {
            await updateBaseNarrativeStatus(baseNarrativeIds, 4, userEmail);
          }
        } catch {
          // Narrative update failure is non-blocking
        }
      }

      // Reload data
      await refreshData(selectedFundGroup, selectedFund, searchValue);

      // Reset selection
      setSelectedShareClasses(new Set());
      setSelectAllMode(false);
      setFundCards((prev) => prev.map((c) => ({
        ...c,
        isSelected: false,
        gridData: c.gridData.map((cl) => ({ ...cl, isSelected: false })),
      })));

      context.handlers.setToast({ type: 'success', body: <>All {documentCount} Share Class{documentCount > 1 ? 'es' : ''} have been approved.</> });
    } catch (err) {
      context.handlers.setToast({ type: 'error', body: <>Error approving {label}: {err.message || 'Unknown error'}</> });
    }
  };

  // ─── Re-open ────────────────────────────────────────────────────────
  const handleOpenContextMenu = (e, fundCode, classCode) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ fundCode, classCode, x: e.clientX, y: e.clientY });
  };

  const handleReopen = async (fundCode, classCode, reportTypeId, reportTypeName) => {
    setContextMenu(null);

    const cl = fundCards
      .find((c) => c.fundCode === fundCode)
      ?.gridData.find((g) => g.classCode === classCode);

    const rts = cl?.reportTypeStatuses?.find((r) => r.ReportTypeId === reportTypeId);
    if (!rts || !rts.IsReportSetupForClass) {
      await showConfirm('No documents to re-open', 'There are no documents to re-open.');
      return;
    }

    const confirmed = await showConfirm(
      `Re-open ${reportTypeName}`,
      `You are about to re-open a ${reportTypeName} document. This will re-open the base and translated languages.`,
    );
    if (!confirmed) return;

    try {
      context.handlers.setToast({ type: 'info', body: <>Re-opening {reportTypeName} for Share Class {classCode}...</> });

      const canonicalTypeName = reportTypes.find((rt) => rt.ReportTypeId === reportTypeId)?.TypeName || reportTypeName;
      if (regulation?.Name === 'PRIIPS') {
        await reopenReport(classCode, fundCode, reportTypeId);
      } else {
        const reopenResponse = await approveOrReopenReportsDurable({
          reportingPeriod: formatRP(reportingPeriod),
          regulation: regulation?.Name,
          reportTypeName: canonicalTypeName,
          action: 'R',
          classCodes: [classCode],
          fundCodes: [],
          userEmail: context.state?.userEmail || 'Unknown',
        });

        const statusQueryGetUri = getDurableStatusQueryUri(reopenResponse);
        if (statusQueryGetUri) {
          context.handlers.setToast({ type: 'info', body: <>Re-open is in progress. Waiting for completion...</> });
          await waitForDurableCompletion(statusQueryGetUri);
        }
      }

      // Update narratives if PRIIPS
      if (regulation?.Name === 'PRIIPS') {
        try {
          const rpFormatted = formatRP(reportingPeriod);
          const userEmail = context.state?.userEmail || 'Unknown';

          const translationsRes = await getTranslationNarratives(rpFormatted, regulation.Name);
          const translations = translationsRes?.value || translationsRes?.Content || [];
          const translatedIds = translations.filter((n) => n.ClassCode === classCode).map((n) => n.TargetNarrativeId || n.NarrativeId);
          if (translatedIds.length > 0) {
            await updateNarrativeTranslationStatusBulk(translatedIds, 2, userEmail);
          }

          const narrativesRes = await getBaseLanguageNarratives(rpFormatted, regulation.Name);
          const narratives = narrativesRes?.value || narrativesRes?.Content || [];
          const baseIds = narratives.filter((n) => n.ClassCode === classCode).map((n) => n.NarrativeId);
          if (baseIds.length > 0) {
            await updateBaseNarrativeStatus(baseIds, 1, userEmail);
          }
        } catch {
          // Narrative update failure is non-blocking
        }
      }

      await refreshData(selectedFundGroup, selectedFund, searchValue);
      context.handlers.setToast({ type: 'success', body: <>Re-opened {reportTypeName} for Share Class {classCode}.</> });
    } catch (err) {
      context.handlers.setToast({ type: 'error', body: <>Error re-opening {reportTypeName}: {err.message || 'Unknown error'}</> });
    }
  };

  const selectedCount = selectedShareClasses.size;
  const hasData = filteredFundCards.length > 0;


  if (loading) {
    return <GrpSpinner />;
  }

  if (!reportingPeriod || !regulation) {
    return (
      <div className={classes.container}>
        <div className={classes.noDataMessage}>
          Please select a Reporting Period and Regulation to view the overview.
        </div>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      {/* Confirm Modal */}
      {confirmModal && (
        <GrpModal
          title={confirmModal.title}
          spinner={false}
          body={<p style={{ margin: 0 }}>{confirmModal.message}</p>}
          footer={
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <GrpButton
                size="Small"
                type="Secondary"
                text="Cancel"
                onClick={() => { confirmModal.onConfirm(false); setConfirmModal(null); }}
              />
              <GrpButton
                size="Small"
                type="Primary"
                text="Confirm"
                onClick={() => { confirmModal.onConfirm(true); setConfirmModal(null); }}
              />
            </div>
          }
          handler={() => { confirmModal.onConfirm(false); setConfirmModal(null); }}
        />
      )}
      {/* Fund Picker / Toolbar */}
      <div className={classes.toolbar}>
        <div className={classes.fundPicker}>
          {fundGroupOptions.length > 1 && (
            <select
              value={selectedFundGroup}
              onChange={handleFundGroupChange}
              className={classes.select}
            >
              {fundGroupOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          )}
          <select
            value={selectedFund}
            onChange={handleFundChange}
            className={classes.select}
          >
            {fundOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        <div className={classes.searchBox}>
          <input
            type="text"
            placeholder="Search Share Classes"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            className={classes.searchInput}
          />
        </div>
      </div>

      {orchestrationStatus && (
        <div
          className={`${classes.orchestrationBanner} ${orchestrationStatus.isFinal ? classes.orchestrationBannerFinal : ''}`}
          role="status"
          aria-live="polite"
        >
          <span className={classes.orchestrationLabel}>{orchestrationStatus.runtimeStatus}</span>
          <span>{orchestrationStatus.message}</span>
        </div>
      )}

      {/* Action Bar */}
      <div className={classes.actionBar} ref={dropdownRef}>
        <div className={classes.actionBarLeft}>
          <label className={classes.filterLabel}>
            <input
              type="checkbox"
              className={classes.checkbox}
              checked={selectAllMode}
              onChange={handleToggleSelectAll}
              disabled={!hasData}
            />
            {' '}Select All
          </label>
          {/* Run Calculations dropdown */}
          <div className={classes.dropdownWrapper}>
            <GrpButton
              size="Small"
              type="Secondary"
              text="Run Calculations ▾"
              onClick={() => setOpenDropdown(openDropdown === 'calc' ? null : 'calc')}
              disabled={!hasData}
            />
            {openDropdown === 'calc' && (
              <div className={classes.dropdownMenu}>
                <div className={classes.dropdownGroupLabel}>R&P Calculations</div>
                <button className={classes.dropdownItem} onClick={() => { setOpenDropdown(null); handleCalculate(true); }}>
                  Run All
                </button>
                <button className={classes.dropdownItem} onClick={() => { setOpenDropdown(null); handleCalculate(false); }} disabled={selectedCount === 0}>
                  Run Selected ({selectedCount})
                </button>
              </div>
            )}
          </div>
          {/* Run Business Rules dropdown */}
          <div className={classes.dropdownWrapper}>
            <GrpButton
              size="Small"
              type="Secondary"
              text="Run Business Rules ▾"
              onClick={() => setOpenDropdown(openDropdown === 'rules' ? null : 'rules')}
              disabled={!hasData}
            />
            {openDropdown === 'rules' && (
              <div className={classes.dropdownMenu}>
                <div className={classes.dropdownGroupLabel}>R&P BRs</div>
                <button className={classes.dropdownItem} onClick={() => { setOpenDropdown(null); handleRunBusinessRules(); }}>
                  Run All
                </button>
              </div>
            )}
          </div>
          {/* Extract Data dropdown (PRIIPS only) */}
          {regulation?.Name !== 'MiFID' && (
          <div className={classes.dropdownWrapper}>
            <GrpButton
              size="Small"
              type="Secondary"
              text="Extract Data ▾"
              onClick={() => setOpenDropdown(openDropdown === 'extract' ? null : 'extract')}
              disabled={!hasData}
            />
            {openDropdown === 'extract' && (
              <div className={classes.dropdownMenu}>
                {['R&P Results', 'Extended R&P Results', 'Annual Returns', 'Published R&P Results', 'Extended Published R&P Results'].map((group) => (
                  <React.Fragment key={group}>
                    <div className={classes.dropdownGroupLabel}>{group}</div>
                    <button className={classes.dropdownItem} onClick={() => { setOpenDropdown(null); handleExtractResults(group, true); }}>
                      Download All - {group}
                    </button>
                    <button className={classes.dropdownItem} onClick={() => { setOpenDropdown(null); handleExtractResults(group, false); }} disabled={selectedCount === 0}>
                      Download Selected ({selectedCount}) - {group}
                    </button>
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>
          )}
        </div>
        <div className={classes.actionBarRight}>
          {/* Generate dropdown */}
          <div className={classes.dropdownWrapper}>
            <GrpButton
              size="Small"
              type="Secondary"
              text="Generate ▾"
              onClick={() => setOpenDropdown(openDropdown === 'generate' ? null : 'generate')}
              disabled={!hasData}
            />
            {openDropdown === 'generate' && (
              <div className={classes.dropdownMenu}>
                {reportTypes.filter((rt) => rt.ReportTypeId !== 11).map((rt) => (
                  <React.Fragment key={rt.ReportTypeId}>
                    <div className={classes.dropdownGroupLabel}>{rt.Label}</div>
                    <button className={classes.dropdownItem} onClick={() => { setOpenDropdown(null); handleGenerate(rt.ReportTypeId, true); }}>
                      Generate All ({getClassCountForReport(rt.ReportTypeId)})
                    </button>
                    <button className={classes.dropdownItem} onClick={() => { setOpenDropdown(null); handleGenerate(rt.ReportTypeId, false); }} disabled={getAvailableClassesForReport(rt.ReportTypeId) === 0}>
                      Generate Selected ({getAvailableClassesForReport(rt.ReportTypeId)})
                    </button>
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>
          {/* Download dropdown */}
          <div className={classes.dropdownWrapper}>
            <GrpButton
              size="Small"
              type="Secondary"
              text="Download ▾"
              onClick={() => setOpenDropdown(openDropdown === 'download' ? null : 'download')}
              disabled={!hasData}
            />
            {openDropdown === 'download' && (
              <div className={classes.dropdownMenu}>
                {reportTypes.filter((rt) => rt.ReportTypeId !== 11).map((rt) => (
                  <React.Fragment key={rt.ReportTypeId}>
                    <div className={classes.dropdownGroupLabel}>{rt.Label}</div>
                    <button className={classes.dropdownItem} onClick={() => { setOpenDropdown(null); handleDownload(rt.ReportTypeId, true); }}>
                      Download All ({getClassCountForReport(rt.ReportTypeId)})
                    </button>
                    <button className={classes.dropdownItem} onClick={() => { setOpenDropdown(null); handleDownload(rt.ReportTypeId, false); }} disabled={getAvailableClassesForReport(rt.ReportTypeId) === 0}>
                      Download Selected ({getAvailableClassesForReport(rt.ReportTypeId)})
                    </button>
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>
          {/* Approve dropdown */}
          <div className={classes.dropdownWrapper}>
            <GrpButton
              size="Small"
              type="Primary"
              text="Approve ▾"
              onClick={() => setOpenDropdown(openDropdown === 'approve' ? null : 'approve')}
              disabled={!hasData}
            />
            {openDropdown === 'approve' && (
              <div className={`${classes.dropdownMenu} ${classes.dropdownMenuRight}`}>
                {reportTypes.filter((rt) => rt.ReportTypeId !== 11).map((rt) => (
                  <React.Fragment key={rt.ReportTypeId}>
                    <div className={classes.dropdownGroupLabel}>{rt.Label}</div>
                    <button className={classes.dropdownItem} onClick={() => { setOpenDropdown(null); handleApprove(rt.ReportTypeId, true); }}>
                      Approve All ({getClassCountForReport(rt.ReportTypeId)})
                    </button>
                    <button className={classes.dropdownItem} onClick={() => { setOpenDropdown(null); handleApprove(rt.ReportTypeId, false); }} disabled={getAvailableClassesForReport(rt.ReportTypeId) === 0}>
                      Approve Selected ({getAvailableClassesForReport(rt.ReportTypeId)})
                    </button>
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Fund Cards */}
      {hasData ? (
        <div className={classes.cardsContainer}>
          {filteredFundCards.map((card) => (
            <div key={card.fundCode} className={classes.fundCard}>
              <div className={classes.fundCardHeader}>
                <div className={classes.fundCardHeaderLeft}>
                  <input
                    type="checkbox"
                    className={classes.checkbox}
                    checked={card.isSelected}
                    onChange={() => toggleFundSelection(card.fundCode)}
                  />
                  <button
                    type="button"
                    className={classes.expandIcon}
                    onClick={() => toggleCardExpand(card.fundCode)}
                    aria-label={card.expanded ? 'Collapse' : 'Expand'}
                  >
                    {card.expanded ? '▼' : '▶'}
                  </button>
                  <span className={classes.fundCode}>{card.fundCode}</span>
                  <span className={classes.fundName}>{card.fundName}</span>
                  <span className={classes.classCount}>{card.shownClassCount} Classes</span>
                </div>
                <div className={classes.fundCardHeaderRight}>
                  {RULE_VALIDATION_TYPES.map((rvt) => {
                    const errors = getValidationCount(card.ruleValidationItems, rvt.key, 'Error');
                    const warnings = getValidationCount(card.ruleValidationItems, rvt.key, 'Warning');
                    return (
                      <div key={rvt.key} className={classes.validationColumn}>
                        <span className={classes.validationLabel}>{rvt.label}</span>
                        <div className={classes.validationCounts}>
                          <div className={classes.validationSubColumn}>
                            <span className={classes.validationLabel}>Errors</span>
                            <span className={classes.errorCount} title="Errors">{errors}</span>
                          </div>
                          <div className={classes.validationSubColumn}>
                            <span className={classes.validationLabel}>Warnings</span>
                            <span className={classes.warningCount} title="Warnings">{warnings}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {card.reportTypeStatuses.map((rts) => (
                    <div key={rts.ReportTypeId} className={classes.reportTypeStatus}>
                      <span className={classes.reportTypeLabel}>{rts.ReportTypeName}</span>
                      {getStatusBadge(deriveHeaderStatus(rts), rts.ApprovedCount ?? -1, rts.CountOfEnabledClassesForThisReport ?? -1)}
                    </div>
                  ))}
                </div>
              </div>

              {card.expanded && (
                <div className={classes.fundCardContent}>
                  {card.loadingClasses && (
                    <div className={classes.loadingText}>LOADING...</div>
                  )}
                  {!card.loadingClasses && card.gridData.length === 0 && (
                    <div className={classes.loadingText}>No classes found.</div>
                  )}
                  {!card.loadingClasses && card.gridData.length > 0 && (
                    <div className={classes.classesGrid}>
                      {card.gridData.map((cl) => (
                        <div key={cl.classCode} className={classes.classRow}>
                          <div className={classes.classRowLeft}>
                            <input
                              type="checkbox"
                              className={classes.checkbox}
                              checked={cl.isSelected}
                              onChange={() => toggleClassSelection(card.fundCode, cl.classCode)}
                            />
                            <span className={classes.classCode}>{cl.classCode}</span>
                            <span className={classes.className}>{cl.className}</span>
                            <button
                              type="button"
                              className={classes.contextMenuBtn}
                              onClick={(e) => handleOpenContextMenu(e, card.fundCode, cl.classCode)}
                              aria-label="More actions"
                            >
                              ⋮
                            </button>
                          </div>
                          <div className={classes.classRowRight}>
                            {RULE_VALIDATION_TYPES.map((rvt) => {
                              const errors = getValidationCount(cl.ruleValidationItems, rvt.key, 'Error');
                              const warnings = getValidationCount(cl.ruleValidationItems, rvt.key, 'Warning');
                              return (
                                <div key={rvt.key} className={classes.validationColumn}>
                                  <div className={classes.validationCounts}>
                                    <div className={classes.validationSubColumn}>
                                      <span className={classes.errorCount} title={`${rvt.label} Errors`}>{errors}</span>
                                    </div>
                                    <div className={classes.validationSubColumn}>
                                      <span className={classes.warningCount} title={`${rvt.label} Warnings`}>{warnings}</span>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                            {cl.reportTypeStatuses.map((rts) => {
                              const { status, message } = deriveDetailStatus(rts, cl.ruleValidationItems);
                              return (
                                <div key={rts.ReportTypeId} className={classes.reportTypeStatus}>
                                  {getStatusBadge(status, -1, -1, message)}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className={classes.noDataMessage}>
          No Funds Available for this Reporting Period.
        </div>
      )}

      {/* Context Menu */}
      {contextMenu && (() => {
        const card = fundCards.find((c) => c.fundCode === contextMenu.fundCode);
        const cl = card?.gridData.find((g) => g.classCode === contextMenu.classCode);
        const menuItems = (cl?.reportTypeStatuses || []).filter((rts) => rts.IsReportSetupForClass);
        return (
          <div
            ref={contextMenuRef}
            className={classes.contextMenuDropdown}
            style={{ top: contextMenu.y, left: contextMenu.x }}
          >
            {menuItems.length === 0 && (
              <div className={classes.contextMenuItem} style={{ color: '#999' }}>No actions available</div>
            )}
            {menuItems.map((rts) => (
              <button
                key={rts.ReportTypeId}
                type="button"
                className={classes.contextMenuItem}
                onClick={() => handleReopen(contextMenu.fundCode, contextMenu.classCode, rts.ReportTypeId, rts.ReportTypeName)}
              >
                Re-open {rts.ReportTypeName}
              </button>
            ))}
          </div>
        );
      })()}
    </div>
  );
};

export default OverviewV2;
